import { useEffect, useState } from 'react';
import { Dots, GhostButton, Screen } from '../components/Chrome.js';
import { formatClock } from '../lib/useSmoothClock.js';
import type { Profile } from '../lib/useGame.js';
import { color } from '../theme.js';

/** The radar sweep from the mockup, with a live elapsed counter. */
export function SearchScreen({
  profile,
  playersInQueue,
  onCancel,
}: {
  profile: Profile | null;
  playersInQueue: number;
  onCancel: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1000), 1000);
    return () => clearInterval(t);
  }, []);

  const initials = (profile?.displayName ?? 'YU').replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase();

  return (
    <Screen brackets="top">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          gap: 'clamp(16px,3cqmin,30px)',
        }}
      >
        {/* radar */}
        <div style={{ position: 'relative', width: 'clamp(150px,40cqmin,300px)', height: 'clamp(150px,40cqmin,300px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid rgba(166,255,46,.5)', animation: 'ssRing 2.4s ease-out infinite' }} />
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid rgba(166,255,46,.5)', animation: 'ssRing 2.4s ease-out infinite 1.2s' }} />
          <div style={{ position: 'absolute', inset: 'clamp(14px,4cqmin,30px)', borderRadius: '50%', border: '1px dashed rgba(166,255,46,.15)' }} />
          <div style={{ position: 'absolute', inset: 'clamp(28px,8cqmin,60px)', borderRadius: '50%', border: '1px solid rgba(166,255,46,.1)' }} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg,rgba(166,255,46,.28),rgba(166,255,46,0) 55%)',
              animation: 'ssSweep 2.6s linear infinite',
              WebkitMaskImage: 'radial-gradient(closest-side,transparent 8%,#000 9%)',
              maskImage: 'radial-gradient(closest-side,transparent 8%,#000 9%)',
            }}
          />
          <div
            style={{
              position: 'relative',
              zIndex: 2,
              width: 'clamp(58px,15cqmin,110px)',
              height: 'clamp(58px,15cqmin,110px)',
              borderRadius: 'clamp(12px,3cqmin,24px)',
              background: 'linear-gradient(150deg,rgba(166,255,46,.25),rgba(166,255,46,.08))',
              border: '1.5px solid rgba(166,255,46,.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 'clamp(22px,5.4cqmin,46px)',
              color: color.limeBright,
              animation: 'ssCore 1.6s ease-in-out infinite',
            }}
          >
            {initials}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 'clamp(18px,4cqmin,32px)', letterSpacing: '.08em' }}>SEARCHING FOR OPPONENT</span>
            <Dots />
          </div>
          <span style={{ fontSize: 'clamp(10px,1.9cqmin,15px)', color: 'rgba(234,255,234,.5)', fontWeight: 500 }}>
            Matching a rival near <span style={{ color: color.lime }}>{profile?.elo ?? '—'} ELO</span> · ranked
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <span style={{ fontSize: 11, letterSpacing: '.24em', color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>ELAPSED</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(22px,4.6cqmin,38px)', lineHeight: 1, textShadow: '0 0 12px rgba(166,255,46,.25)' }}>
              {formatClock(elapsed)}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', border: '1px solid rgba(166,255,46,.15)', borderRadius: 999, background: 'rgba(166,255,46,.04)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color.lime, boxShadow: `0 0 8px ${color.lime}` }} />
            <span style={{ fontSize: 13, color: 'rgba(234,255,234,.7)', fontWeight: 600 }}>
              <span style={{ color: color.text }}>{playersInQueue}</span> in queue
            </span>
          </div>
        </div>

        <GhostButton tone="danger" onClick={onCancel} style={{ marginTop: 8, padding: '14px 34px' }}>
          CANCEL SEARCH
        </GhostButton>
      </div>
    </Screen>
  );
}
